import { isPublicId } from '@idc-forms/id'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import useAuth from '@/features/auth/hooks/use-auth'
import { FEATURES } from '@/shared/config/features'
import { dataProperties } from '@/shared/constants/dataProperties'
import { queryKeys } from '@/shared/constants/query-keys'
import {
  type InventoryCommentView,
  toCommentViewFromV1,
  toCommentViewFromV2
} from '../models/inventory-comment-view'
import {
  createInventoryComment,
  deleteInventoryComment,
  getInventoryComments
} from '../services/inventory.services'
import { inventoryCommentsV2Service } from '../services/inventory-comments-v2.service'

/**
 * ¿Los comentarios de ESTE inmueble hablan v2?
 *
 * DOS CONDICIONES, y las dos hacen falta. El flag es la red de rollback de la ola: apagado, el
 * panel entero vuelve a v1. La forma del id cubre lo que el flag no puede — v2 direcciona por
 * `prp_` y no ofrece ninguna manera de resolver un `_id`, a propósito. Un inmueble que el payload
 * v1 todavía no acompaña con su `prp_` se queda en v1 en vez de romperse.
 *
 * Cuando muera el flag y todo inmueble traiga su `prp_`, esto es siempre `true` y el camino v1 de
 * este archivo se borra entero.
 */
export function inventoryCommentsSpeakV2(propertyPublicId: string | undefined): boolean {
  if (!FEATURES.webAuthV2) return false
  return isPublicId(propertyPublicId, 'prp')
}

type UseInventoryCommentsArgs = {
  /** El `_id` de Mongo — lo que v1 sabe recibir. */
  propertyId: string | null
  /** El `prp_`, cuando el payload lo trajo. */
  propertyPublicId?: string
}

/**
 * El árbol de comentarios del inmueble, ya traducido al view model.
 *
 * Devuelve SIEMPRE la misma forma venga de donde venga, así que quien lo consume no ramifica por
 * versión de API. Las mutaciones que van abajo invalidan la clave del camino que estén usando.
 */
export function useInventoryComments({ propertyId, propertyPublicId }: UseInventoryCommentsArgs) {
  const { auth } = useAuth()
  const speaksV2 = inventoryCommentsSpeakV2(propertyPublicId)

  const v1 = useQuery<InventoryCommentView[]>({
    queryKey: queryKeys.inventoryComments.byProperty(propertyId ?? ''),
    queryFn: async () => {
      if (!propertyId) return []
      const comments = await getInventoryComments(propertyId)
      return comments.map((comment) => toCommentViewFromV1(comment, auth?.[dataProperties.id]))
    },
    refetchOnWindowFocus: false,
    enabled: !speaksV2 && !!propertyId
  })

  const v2 = useQuery<InventoryCommentView[]>({
    queryKey: queryKeys.inventoryComments.byPropertyV2(propertyPublicId ?? ''),
    queryFn: async () => {
      if (!propertyPublicId) return []
      const comments = await inventoryCommentsV2Service.list(
        auth?.v2AccessToken ?? '',
        propertyPublicId
      )
      return comments.map((comment) => toCommentViewFromV2(comment, auth?.publicId))
    },
    refetchOnWindowFocus: false,
    enabled: speaksV2 && !!propertyPublicId
  })

  return speaksV2 ? v2 : v1
}

/** La clave que hay que invalidar para el camino que este inmueble esté usando. */
function activeQueryKey(propertyId: string | null, propertyPublicId: string | undefined) {
  return inventoryCommentsSpeakV2(propertyPublicId)
    ? queryKeys.inventoryComments.byPropertyV2(propertyPublicId ?? '')
    : queryKeys.inventoryComments.byProperty(propertyId ?? '')
}

export function useCreateInventoryComment({
  propertyId,
  propertyPublicId
}: UseInventoryCommentsArgs) {
  const queryClient = useQueryClient()
  const { auth } = useAuth()
  const speaksV2 = inventoryCommentsSpeakV2(propertyPublicId)

  return useMutation({
    mutationFn: async (data: { body: string; parent?: string }) => {
      if (speaksV2 && propertyPublicId) {
        return await inventoryCommentsV2Service.create(
          auth?.v2AccessToken ?? '',
          propertyPublicId,
          data
        )
      }
      if (!propertyId) throw new Error('propertyId requerido')
      return await createInventoryComment(propertyId, data)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: activeQueryKey(propertyId, propertyPublicId)
      })
    }
  })
}

export function useDeleteInventoryComment({
  propertyId,
  propertyPublicId
}: UseInventoryCommentsArgs) {
  const queryClient = useQueryClient()
  const { auth } = useAuth()
  const speaksV2 = inventoryCommentsSpeakV2(propertyPublicId)

  return useMutation({
    // `commentId` ya viene con la identidad correcta: el view model lo llenó con el `_id` o con el
    // `icm_` según de dónde salió el comentario, así que acá no hay nada que adivinar.
    mutationFn: async (commentId: string) => {
      if (speaksV2 && propertyPublicId) {
        await inventoryCommentsV2Service.remove(
          auth?.v2AccessToken ?? '',
          propertyPublicId,
          commentId
        )
        return
      }
      await deleteInventoryComment(commentId)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: activeQueryKey(propertyId, propertyPublicId)
      })
    }
  })
}
